"use client";

import { useState, useEffect } from "react";

interface MarkdownImageProps {
  src: string;
  alt?: string;
}

export function MarkdownImage({ src, alt }: MarkdownImageProps) {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (src.startsWith('/diagrams/') && src.endsWith('.svg')) {
      const filename = src.split('/').pop();

      fetch(`/api/diagrams/${filename}`)
        .then(res => {
          if (!res.ok) throw new Error('Failed to load SVG');
          return res.text();
        })
        .then(content => {
          setSvgContent(content);
          setLoading(false);
        })
        .catch(err => {
          console.error('Failed to load SVG:', src, err);
          setError(true);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [src]);

  // If we have SVG content, render it inline
  if (svgContent) {
    return (
      <div
        className="my-6 flex justify-center bg-gray-50 dark:bg-gray-800 p-4 rounded-lg"
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="my-6 flex justify-center bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <div className="text-center p-8">
          <div className="animate-pulse">
            <div className="h-48 bg-gray-300 dark:bg-gray-700 rounded"></div>
            <p className="text-sm text-gray-500 mt-4">Loading diagram...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error or fallback state
  if (error) {
    return (
      <div className="my-6 flex justify-center bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <div className="text-center p-8 border-2 border-dashed border-red-300 dark:border-red-700 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400 font-medium">Failed to load diagram</p>
          <p className="text-xs text-gray-500 mt-2">{alt || "Diagram"}</p>
          <p className="text-xs text-gray-400 mt-1">{src}</p>
        </div>
      </div>
    );
  }

  // Regular image (not a diagram SVG)
  return (
    <div className="my-6 flex justify-center">
      <img
        src={src}
        alt={alt || "Image"}
        className="max-w-full h-auto rounded-lg border border-gray-200 dark:border-gray-700"
        style={{ maxWidth: '600px', width: '100%', height: 'auto' }}
      />
    </div>
  );
}
